import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RadioContext = createContext(null);
export const useRadio = () => useContext(RadioContext);

export function mediaUrl(src) {
  if (!src) return "";
  return src.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${src}` : src;
}

export function RadioProvider({ children }) {
  const audioRef = useRef(null);
  if (!audioRef.current && typeof Audio !== "undefined") {
    audioRef.current = new Audio();
    audioRef.current.preload = "metadata";
  }
  const bedRef = useRef(null);
  if (!bedRef.current && typeof Audio !== "undefined") {
    bedRef.current = new Audio();
    bedRef.current.preload = "auto";
    bedRef.current.loop = true;
  }
  const bedFadeRef = useRef(null);
  const [tracks, setTracks] = useState([]);
  const [index, setIndex] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [introPlaying, setIntroPlaying] = useState(false);
  const [stationId, setStationId] = useState({ url: "", enabled: false, bedUrl: "", bedVolume: 0.18 });
  const counted = useRef(new Set());
  const introModeRef = useRef(false);
  const pendingIndexRef = useRef(-1);
  const introDoneRef = useRef(false);

  const current = index >= 0 && index < tracks.length ? tracks[index] : null;

  const loadTracks = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/radio/tracks`);
      setTracks(data || []);
      return data || [];
    } catch { return []; }
  }, []);

  useEffect(() => { loadTracks(); }, [loadTracks]);

  useEffect(() => {
    axios.get(`${API}/radio/settings`).then(({ data }) => {
      setStationId({
        url: data.station_id_url || "",
        enabled: !!data.station_id_enabled,
        bedUrl: data.station_id_bed_url || "",
        bedVolume: typeof data.station_id_bed_volume === "number" ? data.station_id_bed_volume : 0.18,
      });
    }).catch(() => {});
  }, []);

  const startBed = useCallback(() => {
    const b = bedRef.current;
    if (!b || !stationId.bedUrl) return;
    if (bedFadeRef.current) { clearInterval(bedFadeRef.current); bedFadeRef.current = null; }
    b.src = mediaUrl(stationId.bedUrl);
    b.currentTime = 0;
    b.volume = stationId.bedVolume;
    b.play().catch(() => {});
  }, [stationId]);

  const stopBed = useCallback((fade = true) => {
    const b = bedRef.current;
    if (!b) return;
    if (bedFadeRef.current) { clearInterval(bedFadeRef.current); bedFadeRef.current = null; }
    if (!fade || b.paused) { b.pause(); return; }
    bedFadeRef.current = setInterval(() => {
      if (b.volume > 0.03) { b.volume = Math.max(0, b.volume - 0.03); }
      else { b.pause(); clearInterval(bedFadeRef.current); bedFadeRef.current = null; }
    }, 40);
  }, []);

  // Wire audio element events
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => { setProgress(a.currentTime || 0); setDuration(a.duration || 0); };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => {
      if (introModeRef.current) {
        introModeRef.current = false;
        setIntroPlaying(false);
        stopBed(true);
        const pi = pendingIndexRef.current;
        pendingIndexRef.current = -1;
        setIndex(pi >= 0 ? pi : -1);
        return;
      }
      setIndex((i) => (tracks.length ? (i + 1) % tracks.length : -1));
    };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onTime);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    a.addEventListener("ended", onEnded);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onTime);
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("ended", onEnded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracks.length]);

  // When index changes, load + play that track's source
  useEffect(() => {
    const a = audioRef.current;
    if (!a || !current) return;
    introModeRef.current = false;
    setIntroPlaying(false);
    stopBed(false);
    const src = mediaUrl(current.audio_url);
    if (!src) return; // external-only track can't play in bar
    a.src = src;
    a.play().catch(() => {});
    if (!counted.current.has(current.id)) {
      counted.current.add(current.id);
      axios.post(`${API}/radio/tracks/${current.id}/play`).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  const playableTracks = useCallback((list) => (list || tracks).filter((t) => t.audio_url), [tracks]);

  const playTrack = useCallback((track, list) => {
    const source = list || tracks;
    const i = source.findIndex((t) => t.id === track.id);
    if (i === -1) return;
    if (list) setTracks(source);
    if (i === index) { toggle(); return; }
    setIndex(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracks, index]);

  const playStation = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    const first = tracks.findIndex((t) => t.audio_url);
    if (stationId.enabled && stationId.url && !introDoneRef.current) {
      introDoneRef.current = true;
      introModeRef.current = true;
      pendingIndexRef.current = first;
      setIndex(-1);
      setIntroPlaying(true);
      a.src = mediaUrl(stationId.url);
      a.play().catch(() => {});
      startBed();
      return;
    }
    if (first >= 0) setIndex(first);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracks, stationId]);

  const toggle = useCallback(() => {
    const a = audioRef.current;
    const b = bedRef.current;
    if (!a) return;
    if (introModeRef.current) {
      if (a.paused) { a.play().catch(() => {}); if (b && b.src) b.play().catch(() => {}); }
      else { a.pause(); if (b) b.pause(); }
      return;
    }
    if (index < 0) { playStation(); return; }
    if (a.paused) a.play().catch(() => {}); else a.pause();
  }, [index, playStation]);

  const pause = useCallback(() => {
    const a = audioRef.current;
    if (a && !a.paused) a.pause();
    stopBed(false);
  }, [stopBed]);

  const step = useCallback((dir) => {
    if (!tracks.length) return;
    let i = index;
    for (let n = 0; n < tracks.length; n++) {
      i = (i + dir + tracks.length) % tracks.length;
      if (tracks[i].audio_url) { setIndex(i); return; }
    }
  }, [tracks, index]);

  const seek = useCallback((pct) => {
    const a = audioRef.current;
    if (a && a.duration) a.currentTime = pct * a.duration;
  }, []);

  const value = {
    tracks, current, index, playing, progress, duration, introPlaying,
    loadTracks, playTrack, playStation, toggle, pause,
    next: () => step(1), prev: () => step(-1), seek,
  };
  return <RadioContext.Provider value={value}>{children}</RadioContext.Provider>;
}
